import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Safety net for a known Radix UI bug where a Dialog/Select/Dropdown can leave
 * `pointer-events: none` (and scroll/overflow locks) stuck on <body> after it
 * closes, freezing ALL clicks (navigation appears broken until a page refresh).
 *
 * We clear the stuck lock the instant it appears while no Radix overlay is open,
 * using a MutationObserver (immediate) plus an interval + route-change reset.
 */
function hasOpenOverlay() {
  return !!document.querySelector(
    '[data-state="open"][role="dialog"], [data-state="open"][role="menu"], [data-state="open"][role="listbox"], [data-radix-popper-content-wrapper], [data-radix-select-content]'
  );
}

function unstick() {
  if (hasOpenOverlay()) return;
  const b = document.body;
  if (b.style.pointerEvents === 'none') b.style.pointerEvents = '';
  if (b.style.overflow === 'hidden') b.style.overflow = '';
  if (b.hasAttribute('data-scroll-locked')) b.removeAttribute('data-scroll-locked');
}

export default function useBodyPointerGuard() {
  const { pathname } = useLocation();

  // Reset on every navigation
  useEffect(() => {
    unstick();
    const t = setTimeout(unstick, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Immediate observer + interval fallback
  useEffect(() => {
    // Lightweight: only watch <body> style / scroll-lock attributes. When Radix
    // opens/closes an overlay it toggles these, so this fires exactly when needed.
    const observer = new MutationObserver(() => {
      requestAnimationFrame(unstick);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['style', 'data-scroll-locked'] });
    const id = setInterval(unstick, 250);
    return () => {
      observer.disconnect();
      clearInterval(id);
    };
  }, []);
}
